import React, { useState, useMemo, useCallback } from "react";
import { toast } from "sonner";
import api from "@/api";
import { usePagination } from "@/lib/usePagination";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Search,
  CheckCircle2,
  AlertCircle,
  ClipboardList,
  Receipt,
  FileSignature,
  Edit,
  Filter,
  X,
  Printer,
  Download,
  MoreHorizontal,
  Building2,
  Users,
  Euro,
} from "lucide-react";
import { currency, formatDate } from "@/utils/helpers";
import { cn } from "@/lib/utils";
import XLSX from "xlsx-js-style";
import { applyRegisterStyle, writeFileWithPageSetup, fileDate } from "@/utils/excelStyle";

/**
 * Da Fatturare Tab Component
 * Table layout matching FattureTab for consistency
 * Default: Gestione Agenzia, sorted descending by date
 */
export default function DaFatturareTab({
  clients,
  casesToBill,
  readyToBill,
  missingBillingData,
  totalToBill,
  searchQuery,
  setSearchQuery,
  filterClient,
  setFilterClient,
  filterPriority,
  setFilterPriority,
  filterBillingStatus,
  setFilterBillingStatus,
  handleEditCase,
  createInvoiceFromCase,
  defaultManagementFilter = "agenzia"
}) {
  const [showFilters, setShowFilters] = useState(false);
  const [gestioneFilter, setGestioneFilter] = useState(defaultManagementFilter);

  // Enrich cases with client data
  const enrichedCases = useMemo(() => {
    return casesToBill.map(c => {
      const client = clients.find(cl => cl.id === c.clientId);
      const managementType = c.practiceType === "Gestionale Studio" ? "studio" : "agenzia";
      const fee = c._billing?.residualAmount ?? c.finalFee ?? c.estimatedFee ?? 0;
      const hasFee = fee > 0;
      return {
        ...c,
        clientName: c._isClientDeadline || c._isBillingItem ? c.clientName : (client?.fullName || "Cliente non trovato"),
        managementType,
        fee,
        hasFee,
        isClientDeadline: !!c._isClientDeadline,
        isBillingItem: !!c._isBillingItem,
      };
    });
  }, [casesToBill, clients]);

  // Apply filters and sort descending by openingDate
  const filteredCases = useMemo(() => {
    return enrichedCases
      .filter(c => {
        if (searchQuery) {
          const q = searchQuery.toLowerCase();
          if (
            !c.caseNumber?.toLowerCase().includes(q) &&
            !c.subject?.toLowerCase().includes(q) &&
            !c.practiceArea?.toLowerCase().includes(q) &&
            !c.clientName?.toLowerCase().includes(q)
          ) return false;
        }
        if (filterClient !== "all" && String(c.clientId) !== filterClient) return false;
        if (filterPriority !== "all" && c.priority !== filterPriority) return false;
        if (gestioneFilter !== "all" && c.managementType !== gestioneFilter) return false;
        if (filterBillingStatus === "ready" && !c.hasFee) return false;
        if (filterBillingStatus === "missing" && c.hasFee) return false;
        return true;
      })
      .sort((a, b) => {
        const dateA = a.openingDate || a.createdAt || '';
        const dateB = b.openingDate || b.createdAt || '';
        return dateB.localeCompare(dateA);
      });
  }, [enrichedCases, searchQuery, filterClient, filterPriority, gestioneFilter, filterBillingStatus]);

  // Totals
  const totals = useMemo(() => {
    const ready = filteredCases.filter(c => c.hasFee);
    const missing = filteredCases.filter(c => !c.hasFee);
    const total = ready.reduce((sum, c) => sum + c.fee, 0);
    return { ready: ready.length, missing: missing.length, total, all: filteredCases.length };
  }, [filteredCases]);

  // Paginazione (20/02/2026).
  const {
    paged: pagedCases,
    paginationBar: dfPaginationBar,
  } = usePagination(filteredCases, {
    storageKey: "da-fatturare",
    itemLabelSingular: "pratica",
    itemLabelPlural: "pratiche",
  });

  // Unique clients for filter
  const uniqueClients = useMemo(() => {
    const ids = [...new Set(casesToBill.map(c => c.clientId))];
    return ids.map(id => clients.find(cl => cl.id === id)).filter(Boolean);
  }, [casesToBill, clients]);

  const hasActiveFilters = searchQuery || filterClient !== "all" || filterPriority !== "all" ||
    gestioneFilter !== "all" || filterBillingStatus !== "all";

  const resetFilters = () => {
    setSearchQuery("");
    setFilterClient("all");
    setFilterPriority("all");
    setGestioneFilter("all");
    setFilterBillingStatus("all");
  };

  // Print list
  const handlePrintList = useCallback(() => {
    const gestioneLabel = gestioneFilter === "all" ? "Tutte" :
      gestioneFilter === "studio" ? "Studio" : "Agenzia";

    const printContent = `<!DOCTYPE html><html><head><title>Da Fatturare</title>
      <style>
        body{font-family:Arial,sans-serif;padding:20px;font-size:12px}
        h1{font-size:18px;margin-bottom:5px}
        .sub{color:#666;font-size:11px;margin-bottom:15px}
        table{width:100%;border-collapse:collapse;font-size:11px}
        th,td{border:1px solid #ddd;padding:6px 8px;text-align:left}
        th{background:#f5f5f5;font-weight:bold}
        .r{text-align:right}
        .badge{display:inline-block;padding:2px 6px;border-radius:3px;font-size:10px}
        .ready{background:#dcfce7;color:#166534}
        .missing{background:#fef3c7;color:#92400e}
        @media print{body{padding:0}}
      </style></head><body>
      <h1>Pratiche Da Fatturare</h1>
      <p class="sub">Gestione: ${gestioneLabel} | ${filteredCases.length} pratiche | Stampato il ${new Date().toLocaleDateString('it-IT')}</p>
      <table><thead><tr>
        <th>N. Pratica</th><th>Gestione</th><th>Data</th><th>Cliente</th>
        <th>Oggetto</th><th>Tipo</th><th class="r">Compenso</th><th>Stato</th>
      </tr></thead><tbody>
      ${filteredCases.map(c => `<tr>
        <td>${c.caseNumber || '-'}</td>
        <td>${c.managementType === 'studio' ? 'Studio' : 'Agenzia'}</td>
        <td>${formatDate(c.openingDate)}</td>
        <td>${c.clientName}</td>
        <td>${c.subject || '-'}</td>
        <td>${c.practiceArea || '-'}</td>
        <td class="r">${c.hasFee ? currency(c.fee) : '-'}</td>
        <td><span class="badge ${c.hasFee ? 'ready' : 'missing'}">${c.hasFee ? 'Pronta' : 'Dati mancanti'}</span></td>
      </tr>`).join('')}
      </tbody></table>
      <script>window.onload=function(){window.print()}</script>
      </body></html>`;

    const w = window.open('', '_blank');
    if (!w) { alert('Popup bloccato'); return; }
    w.document.open();
    w.document.write(printContent);
    w.document.close();
  }, [filteredCases, gestioneFilter]);

  // Export Excel - professional register
  const handleExportExcel = useCallback(() => {
    const exportDateStr = new Date().toLocaleDateString('it-IT');
    const gestioneLabel = gestioneFilter === "all" ? "Tutte le Gestioni" :
      gestioneFilter === "studio" ? "Gestione Studio" : "Gestione Agenzia";

    const numCols = 9;
    const dataHeaders = ["N. Pratica", "Data", "Cliente", "Gestione", "Oggetto", "Tipo", "Compenso", "Stato", "Priorita"];
    const fmt2dec = (val) => Math.round((Number(val) || 0) * 100) / 100;
    const fmtDate = (d) => {
      if (!d) return '';
      try { return new Date(d).toLocaleDateString('it-IT'); } catch { return d; }
    };

    const dataRows = filteredCases.map(c => [
      c.caseNumber || '',
      fmtDate(c.openingDate),
      c.clientName || '',
      c.managementType === 'studio' ? 'Studio' : 'Agenzia',
      c.subject || '',
      c.practiceArea || '',
      c.hasFee ? fmt2dec(c.fee) : 0,
      c.hasFee ? 'Pronta' : 'Incompleta',
      c.priority || ''
    ]);

    const emptyRow = Array(numCols).fill("");
    const headerData = [
      ["Da Fatturare", ...Array(numCols - 1).fill("")],
      ["Gruppo Impresa STP", ...Array(numCols - 1).fill("")],
      [`${gestioneLabel}  |  Periodo: dal 01/01/2026 al ${exportDateStr}  |  Esportato il: ${exportDateStr}`, ...Array(numCols - 1).fill("")],
      emptyRow,
      dataHeaders,
      ...dataRows
    ];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(headerData);
    ws['!cols'] = [
      { wch: 14 }, { wch: 14 }, { wch: 30 }, { wch: 12 },
      { wch: 32 }, { wch: 18 }, { wch: 14 }, { wch: 14 }, { wch: 10 },
    ];
    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: numCols - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: numCols - 1 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: numCols - 1 } },
    ];
    
    applyRegisterStyle(ws, numCols, 4, headerData.length, [6], [0, 1, 3]);
    XLSX.utils.book_append_sheet(wb, ws, "Da Fatturare");
    writeFileWithPageSetup(wb, `da_fatturare_${fileDate()}.xlsx`, 84);
  }, [filteredCases, gestioneFilter]);

  return (
    <div className="space-y-4">
      {/* Header with Stats */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <ClipboardList className="h-5 w-5 text-green-600" />
                Da Fatturare
              </CardTitle>
              <p className="text-sm text-slate-500 mt-1">
                {filteredCases.length} di {casesToBill.length} pratiche
                {hasActiveFilters && " (filtrate)"}
              </p>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-xs text-slate-500">Pronte</p>
                <p className="text-lg font-bold text-green-600">{totals.ready}</p>
              </div>
              <Separator orientation="vertical" className="h-10" />
              <div className="text-center">
                <p className="text-xs text-slate-500">Dati mancanti</p>
                <p className="text-lg font-bold text-orange-600">{totals.missing}</p>
              </div>
              <Separator orientation="vertical" className="h-10" />
              <div className="text-center">
                <p className="text-xs text-slate-500">Totale da fatturare</p>
                <p className="text-lg font-bold text-slate-900">{currency(totals.total)}</p>
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Filters Card */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Cerca per numero, cliente, oggetto..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-billing-input"
              />
            </div>
            <Button
              variant={showFilters ? "secondary" : "outline"}
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="gap-2"
              data-testid="toggle-billing-filters-btn"
            >
              <Filter className="h-4 w-4" />
              Filtri
              {hasActiveFilters && (
                <Badge className="ml-1 bg-blue-600 text-white text-xs px-1.5">!</Badge>
              )}
            </Button>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={resetFilters} className="text-slate-500 hover:text-slate-700">
                <X className="h-4 w-4 mr-1" />
                Reset
              </Button>
            )}

            <div className="flex-1" />

            {/* 1. Stampa - FIRST button */}
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrintList}
              disabled={filteredCases.length === 0}
              className="gap-2"
              data-testid="print-billing-list-btn"
              title={`Stampa ${filteredCases.length} righe filtrate`}
            >
              <Printer className="h-4 w-4" />
              Stampa
              <span className="ml-1 text-xs opacity-70">({filteredCases.length})</span>
            </Button>
            {/* 2. Esporta Excel - SECOND button */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportExcel}
              disabled={filteredCases.length === 0}
              className="gap-2"
              data-testid="export-billing-excel-btn"
              title={`Esporta ${filteredCases.length} righe filtrate`}
            >
              <Download className="h-4 w-4" />
              Esporta Excel
              <span className="ml-1 text-xs opacity-70">({filteredCases.length})</span>
            </Button>
          </div>

          {/* Expanded Filters */}
          <div className={cn(
            "grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-slate-50 rounded-lg border",
            showFilters ? "" : "hidden"
          )}>
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Cliente</Label>
              <Select value={filterClient} onValueChange={setFilterClient}>
                <SelectTrigger className="h-9" data-testid="filter-billing-client">
                  <SelectValue placeholder="Tutti" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i clienti</SelectItem>
                  {uniqueClients.map(client => (
                    <SelectItem key={client.id} value={String(client.id)}>
                      {client.fullName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Gestione</Label>
              <Select value={gestioneFilter} onValueChange={setGestioneFilter}>
                <SelectTrigger className="h-9" data-testid="filter-billing-gestione">
                  <SelectValue placeholder="Tutte" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte</SelectItem>
                  <SelectItem value="studio">
                    <div className="flex items-center gap-2"><Building2 className="h-3 w-3" />Studio</div>
                  </SelectItem>
                  <SelectItem value="agenzia">
                    <div className="flex items-center gap-2"><Users className="h-3 w-3" />Agenzia</div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Priorita</Label>
              <Select value={filterPriority} onValueChange={setFilterPriority}>
                <SelectTrigger className="h-9" data-testid="filter-billing-priority">
                  <SelectValue placeholder="Tutte" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte</SelectItem>
                  <SelectItem value="Alta">Alta</SelectItem>
                  <SelectItem value="Media">Media</SelectItem>
                  <SelectItem value="Bassa">Bassa</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Stato Dati</Label>
              <Select value={filterBillingStatus} onValueChange={setFilterBillingStatus}>
                <SelectTrigger className="h-9" data-testid="filter-billing-status">
                  <SelectValue placeholder="Tutti" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="ready">Pronte</SelectItem>
                  <SelectItem value="missing">Dati Mancanti</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="w-[120px] font-semibold">N. Pratica</TableHead>
                  <TableHead className="w-[80px] font-semibold text-center">Gestione</TableHead>
                  <TableHead className="w-[90px] font-semibold">Data</TableHead>
                  <TableHead className="font-semibold">Cliente</TableHead>
                  <TableHead className="font-semibold">Oggetto</TableHead>
                  <TableHead className="w-[120px] font-semibold">Tipo</TableHead>
                  <TableHead className="w-[100px] font-semibold text-right">Compenso</TableHead>
                  <TableHead className="w-[100px] font-semibold text-center">Stato</TableHead>
                  <TableHead className="w-[70px] font-semibold text-center">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCases.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="h-32 text-center">
                      <div className="flex flex-col items-center justify-center text-slate-400">
                        <ClipboardList className="h-10 w-10 mb-2 opacity-50" />
                        <p>{hasActiveFilters ? "Nessuna pratica con questi filtri" : "Nessuna pratica da fatturare"}</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedCases.map(c => (
                    <TableRow
                      key={c.id}
                      className="group hover:bg-slate-50 transition-colors"
                      data-testid={`billable-case-${c.id}`}
                    >
                      <TableCell className="font-medium font-mono text-sm">
                        {c.isClientDeadline
                          ? <Badge className="text-[10px] bg-orange-50 text-orange-600 border-orange-200">Scadenza</Badge>
                          : c.isBillingItem
                          ? <Badge className="text-[10px] bg-blue-50 text-blue-600 border-blue-200">Piano</Badge>
                          : (c.caseNumber || '-')
                        }
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge
                          variant="outline"
                          className={cn(
                            "text-[10px]",
                            c.managementType === "studio"
                              ? "border-blue-300 text-blue-700"
                              : "border-purple-300 text-purple-700"
                          )}
                        >
                          {c.managementType === "studio" ? "S" : "A"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-600 text-sm">{formatDate(c.openingDate)}</TableCell>
                      <TableCell>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="truncate max-w-[180px] block cursor-help text-sm">
                                {c.clientName}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent><p>{c.clientName}</p></TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600 truncate max-w-[200px]">{c.subject || '-'}</TableCell>
                      <TableCell className="text-sm text-slate-500">{c.practiceArea || '-'}</TableCell>
                      <TableCell className="text-right font-mono text-sm font-semibold">
                        {c.hasFee ? (
                          <div>
                            <div>{currency(c.fee)}</div>
                            {c._billing?.partiallyInvoiced && (
                              <div className="text-[10px] font-sans font-normal text-slate-500">
                                Pattuito {currency(c._billing.agreedAmount)} ·
                                acconti {currency(c._billing.invoicedAmount)} ·
                                {c._billing.mode === "lordo" ? " lordo" : " netto"}
                              </div>
                            )}
                          </div>
                        ) : <span className="text-slate-400">-</span>}
                      </TableCell>
                      <TableCell className="text-center">
                        {c.hasFee ? (
                          <Badge className="bg-green-100 text-green-700 border-green-300 text-xs">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Pronta
                          </Badge>
                        ) : (
                          <Badge className="bg-orange-100 text-orange-700 border-orange-300 text-xs">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Incompleta
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                data-testid={`billing-actions-${c.id}`}
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              {c.hasFee && (
                                <DropdownMenuItem
                                  onClick={() => createInvoiceFromCase(c, "invoice")}
                                  className="gap-2"
                                  data-testid={`create-invoice-${c.id}`}
                                >
                                  <Receipt className="h-4 w-4" />
                                  Crea Fattura
                                </DropdownMenuItem>
                              )}
                              {c.hasFee && (
                                <DropdownMenuItem
                                  onClick={() => createInvoiceFromCase(c, "proforma")}
                                  className="gap-2"
                                  data-testid={`create-proforma-${c.id}`}
                                >
                                  <FileSignature className="h-4 w-4" />
                                  Crea Documento Proforma
                                </DropdownMenuItem>
                              )}
                              {!c.isClientDeadline && !c.isBillingItem && (
                                <DropdownMenuItem
                                  onClick={() => handleEditCase(c)}
                                  className="gap-2"
                                >
                                  <Edit className="h-4 w-4" />
                                  {c.hasFee ? "Modifica Pratica" : "Completa Dati"}
                                </DropdownMenuItem>
                              )}
                              {/* 11/06/2026 — "Rimuovi da Da Fatturare"
                                  (richiesta utente, punto 4). Visibile
                                  per le pratiche reali quando non hanno
                                  fee valorizzata (Incomplete) o quando
                                  l'operatore decide di non emettere
                                  alcuna fattura. Setta `billingExcluded`
                                  sul record senza eliminare la pratica
                                  storica. */}
                              {!c.isClientDeadline && !c.isBillingItem && (
                                <DropdownMenuItem
                                  onClick={async () => {
                                    const reason = window.prompt(
                                      `Rimuovi "${c.subject}" dalla coda Da Fatturare.\n\n` +
                                      "La pratica resterà nel Registro, ma non comparirà più qui.\n" +
                                      "Inserisci la motivazione (facoltativa):"
                                    );
                                    if (reason === null) return;
                                    try {
                                      await api.request(`/api/cases/${c.id}/billing-exclude`, {
                                        method: "PATCH",
                                        body: JSON.stringify({
                                          billingExcluded: true,
                                          billingExcludedReason: reason || "",
                                          billingExcludedAt: new Date().toISOString(),
                                        }),
                                      });
                                      toast.success("Pratica rimossa da Da Fatturare");
                                      if (typeof window !== "undefined") {
                                        window.dispatchEvent(new CustomEvent("cases:reload"));
                                      }
                                    } catch (e) {
                                      toast.error(e?.message || "Errore rimozione");
                                    }
                                  }}
                                  className="gap-2 text-red-600 focus:text-red-700 focus:bg-red-50"
                                  data-testid={`remove-from-billing-${c.id}`}
                                >
                                  <FileSignature className="h-4 w-4" />
                                  Rimuovi da Da Fatturare
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {dfPaginationBar}

          {/* Footer */}
          <div className={cn(
            "border-t bg-slate-50 p-4",
            filteredCases.length === 0 && "hidden"
          )}>
            <div className="flex items-center justify-end gap-6 text-sm">
              <div className="text-slate-600">
                <span className="font-medium">{filteredCases.length}</span> pratiche
              </div>
              <Separator orientation="vertical" className="h-6" />
              <div>
                <span className="text-slate-500">Pronte:</span>
                <span className="ml-2 font-semibold text-green-600">{totals.ready}</span>
              </div>
              <div>
                <span className="text-slate-500">Incomplete:</span>
                <span className="ml-2 font-semibold text-orange-600">{totals.missing}</span>
              </div>
              <div>
                <span className="text-slate-500">Totale:</span>
                <span className="ml-2 font-bold text-slate-900">{currency(totals.total)}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
